/**
 * "Overlay (Group)" blocks (.group-overlay-block) sit on top of an image
 * using position: absolute (often centered with a CSS transform), so the
 * banner section never grows to fit them on its own. When content inside
 * one changes height (a Klaviyo form moving through its steps, or text
 * reflowing on resize), it can overflow past the section and look cut off.
 *
 * This watches every overlay box and grows the right container's
 * min-height just enough to fit it, so the banner stays the height of its
 * image until content actually needs more room, then adapts. It shrinks
 * back down automatically if the content gets shorter again.
 *
 * Overlays can be nested inside other overlays, and getBoundingClientRect
 * (not offsetTop/offsetHeight) is used because it reflects the actual
 * transform-adjusted position — offsetTop ignores CSS transforms, which
 * are how these overlays get centered, and using it overstates the height needed.
 *
 * Embedded form apps (like Klaviyo) frequently rebuild their own markup
 * on each step instead of just resizing it, and can render asynchronously
 * after the page has already loaded. A ResizeObserver alone can miss
 * those cases, so this also re-checks on any DOM mutation nearby and a
 * few times shortly after load as a safety net.
 */
(function () {
  var SELECTOR = '.group-overlay-block';

  function findContainer(overlay) {
    var node = overlay.offsetParent;
    while (node && node.classList.contains('group-overlay-block')) {
      node = node.offsetParent;
    }
    return node;
  }

  function adapt() {
    var overlays = document.querySelectorAll(SELECTOR);
    if (!overlays.length) return;

    var containers = new Set();
    overlays.forEach(function (overlay) {
      var container = findContainer(overlay);
      if (container) containers.add(container);
    });

    // Reset first so containers can shrink back down when content shrinks.
    containers.forEach(function (container) {
      container.style.minHeight = '';
    });

    var needed = new Map();
    overlays.forEach(function (overlay) {
      var container = findContainer(overlay);
      if (!container) return;

      var overlayRect = overlay.getBoundingClientRect();
      var containerRect = container.getBoundingClientRect();
      var bottom = overlayRect.bottom - containerRect.top;

      var current = needed.get(container) || 0;
      if (bottom > current) needed.set(container, bottom);
    });

    needed.forEach(function (height, container) {
      if (height > container.clientHeight) {
        container.style.minHeight = height + 'px';
      }
    });
  }

  function init() {
    adapt();

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(adapt);
      document.querySelectorAll(SELECTOR).forEach(function (overlay) {
        ro.observe(overlay);
      });
    }

    if ('MutationObserver' in window) {
      var scheduled = false;
      var mo = new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(function () {
          scheduled = false;
          adapt();
        });
      });

      document.querySelectorAll(SELECTOR).forEach(function (overlay) {
        mo.observe(overlay, { childList: true, subtree: true, attributes: true });
      });
    }

    // Klaviyo (and similar embeds) can render asynchronously after load.
    [250, 750, 1500, 3000].forEach(function (delay) {
      setTimeout(adapt, delay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('resize', adapt);
})();
